/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react'
import { Modal, Image, Alert, Dropdown, App } from 'antd'
import { useLocation, Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import { Input } from 'antd'
import { HeartOutlined, HeartFilled, DeleteOutlined } from '@ant-design/icons'
import sportninjaLogo from './sportninja-post.png'
import canlanLogo from './canlan-post.png'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  getFeedPostDate,
  getImageThumbnailId,
} from '@sportninja/common/utils/utils'
import VerifiedIcon from './VerifiedIcon'
import { paragraphMedium, weightRegular } from '../css'
import {
  Reply,
  Image as ImageInterface,
} from '@sportninja/common/hooks/useSocialFeed'
import { MoreIcon } from './MoreIcon'
import DeleteModal from '../Modal/DeleteModal'
import req from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import ExternalLinkIcon from './ExternalLinkIcon'
import ShareIcon from './ShareIcon'
import { ROUTES } from '@sportninja/common/constants/app'
import RichTextRenderer from '../RichTextRenderer'
import ShareModal from '../ShareModal'
import { ProfileMetadataRow } from './ProfileLink'
import { buildPostShareUrl } from '@sportninja/common/features/social/helpers/buildPostShareUrl'
const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    // backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

// API functions for comment interactions
const likeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'POST',
  })
  return true
}

const dislikeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'DELETE',
  })
  return true
}

const deleteComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}`, {
    method: 'DELETE',
  })
  return true
}

// Constants for placeholder images
const PLACEHOLDER_IMAGES = {
  user: '/images/generic_placeholders/placeholder_player.png',
  organization: '/images/generic_placeholders/placeholder_organization.png',
  team: '/images/generic_placeholders/placeholder_team.png',
  schedule: '/images/generic_placeholders/placeholder_competition.png',
} as const

// Utility function to get placeholder image
const getPlaceholderImage = (
  entityType: 'team' | 'schedule' | 'organization',
  isUser = false
): string => {
  if (isUser) {
    return PLACEHOLDER_IMAGES.user
  }
  return PLACEHOLDER_IMAGES[entityType]
}

// Utility function to parse URL host
const parseUrlHost = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.host.replace(/^www\./, '')
  } catch (error) {
    console.error('Error parsing URL:', error)
    return url
  }
}

// Utility function to generate entity link
const getEntityLink = (
  entityType: 'team' | 'schedule' | 'organization',
  entityId: string
): string => {
  const entityRouteMap = {
    organization: ROUTES.ORG_ROOT,
    team: ROUTES.TEAM_ROOT,
    schedule: ROUTES.SCHEDULE_ROOT,
  }

  return `${entityRouteMap[entityType]}/${entityId}`
}

// Styled components
const modalContentStyles = css`
  overflow: hidden;
  border-radius: 8px;
  display: flex;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 0;
  }
`

const imageContainerStyles = css`
  flex: 1;
  height: 100%;
  position: relative;
  background: ${colors.NEUTRAL_900};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    height: 40vh;
    min-height: 250px;
    flex: none;
  }

  .ant-image {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #000000 !important;
  }
  .ant-image .ant-image-img {
    max-height: 100% !important;
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
  }
`

const contentContainerStyles = css`
  width: 400px;
  flex-shrink: 0;
  height: 100%;
  padding: 16px;
  background: ${colors.SECONDARY_300};
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
    height: 60vh;
    flex: 1;
    padding: 16px;
    overflow-y: auto;
  }
`

const contentOnlyContainerStyles = css`
  min-width: 500px;
  width: 100%;
  height: auto;
  min-height: 500px;
  max-height: 90vh;
  padding: 24px;
  background: ${colors.SECONDARY_300};
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    padding: 16px;
    min-height: unset;
    overflow-y: auto;
  }
`

const headerStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`

const profileInfoStyles = css`
  flex: 1;
  display: flex;
  gap: 8px;
`

const avatarStyles = css`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: contain;
`

const avatarLinkStyles = css`
  display: inline-block;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const nameLinkStyles = css`
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const userInfoStyles = css`
  display: flex;
  flex-direction: column;
`

const nameRowStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const separatorStyles = css`
  height: 1px;
  background: ${colors.SECONDARY_200};
  width: 100%;
`

const postContentStyles = (_canReply: boolean) => css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: none;
`

const commentsContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const commentsListStyles = css`
  align-self: stretch;
  overflow: hidden;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: inline-flex;
`

const commentItemStyles = css`
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
  min-width: 0;
`

const commentContentColumnStyles = css`
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
  width: 100%;
  min-width: 0;
`

const commentNameTimeRowStyles = css`
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  display: inline-flex;
`

const commentNameContainerStyles = css`
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: flex;
`

const commentNameAndTimeStyles = css`
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
`

const commentActionsRowStyles = css`
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
`

const commentTextContainerStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const commentMoreIconContainerStyles = css`
  display: flex;
  align-items: center;
`

const commentAvatarStyles = css`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  border: 1px solid ${colors.WHITE};
  object-fit: contain;
`

const commentTextStyles = css`
  align-self: stretch;
  color: ${colors.WHITE};
  font-size: 14px;
  font-family: 'Inter';
  font-weight: 400;
  line-height: 20.3px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  width: 100%;
`

const likeButtonStyles = css`
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  background: ${colors.SECONDARY_200};
  border-radius: 8px;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  display: flex;
  cursor: pointer;
`

const commentSeparatorStyles = css`
  align-self: stretch;
  height: 0;
  outline: 1px ${colors.SECONDARY_200} solid;
  outline-offset: -0.5px;
  margin-top: 8px;
  margin-bottom: 8px;
`

const actionsContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

const actionsGroupStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const actionButtonStyles = css`
  padding: 8px;
  background: ${colors.SECONDARY_200};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

const linkButtonStyles = css`
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.PRIMARY};
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
`

const scrollableContentStyles = css`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.SECONDARY_200};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.NEUTRAL_50};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.NEUTRAL_30};
  }
`

const addCommentContainerStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 0;
  }
`

const commentInputContainerStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const postButtonStyles = css`
  padding: 8px 16px;
  background: ${colors.PRIMARY};
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;

  &:hover {
    background: ${colors.PRIMARY};
    opacity: 0.9;
  }

  &:disabled {
    background: ${colors.SECONDARY_100};
    cursor: not-allowed;
    opacity: 0.5;
  }
`

// Comment component
const CommentItem = memo<{
  comment: Reply
  onLikeClick: (commentId: string) => void
  onDislikeClick: (commentId: string) => void
  onDeleteClick?: (commentId: string) => void
  selfUserId?: string
  selfUserName?: string
}>(
  ({
    comment,
    onLikeClick,
    onDislikeClick,
    onDeleteClick,
    selfUserId,
    selfUserName,
  }) => {
    const [isLiked, setIsLiked] = useState(comment.is_liked || false)
    const [likesCount, setLikesCount] = useState(comment.like_count || 0)

    const handleLikeClick = useCallback(() => {
      const newIsLiked = !isLiked
      setIsLiked(newIsLiked)
      setLikesCount(newIsLiked ? likesCount + 1 : likesCount - 1)

      if (newIsLiked) {
        onLikeClick(comment.id)
      } else {
        onDislikeClick(comment.id)
      }
    }, [isLiked, likesCount, comment.id, onLikeClick, onDislikeClick])

    const avatarSrc = useMemo(() => {
      return getImageThumbnailId(comment.user) || PLACEHOLDER_IMAGES.user
    }, [comment.user])

    const formattedDate = useMemo(() => {
      return getFeedPostDate(comment.created_at)
    }, [comment.created_at])

    const displayName = useMemo(() => {
      if (selfUserId && comment.user?.id === selfUserId) {
        return selfUserName || comment.user?.name_full || 'You'
      }
      return comment.user?.name_full || 'Unknown User'
    }, [comment.user, selfUserId, selfUserName])

    const canDelete = useMemo(() => {
      return selfUserId && comment.user?.id === selfUserId
    }, [selfUserId, comment.user?.id])

    // Dropdown menu items for comment actions
    const dropdownItems = useMemo(() => {
      if (!canDelete) {
        return []
      }

      return [
        {
          label: 'Delete',
          key: 'delete',
          icon: <DeleteOutlined />,
          onClick: () => onDeleteClick?.(comment.id),
          danger: true,
        },
      ]
    }, [canDelete, onDeleteClick, comment.id])

    const renderMoreIcon = () => {
      if (canDelete && dropdownItems.length > 0) {
        return (
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={['click']}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
            overlayStyle={{ zIndex: 10002 }}
          >
            <span
              css={css`
                cursor: pointer;
                align-self: center;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <MoreIcon color={colors.NEUTRAL_50} />
            </span>
          </Dropdown>
        )
      }

      // Don't render anything if user doesn't have delete permission
      return null
    }

    return (
      <div css={commentItemStyles}>
        {/* Avatar */}
        <img src={avatarSrc} alt={displayName} css={commentAvatarStyles} />

        {/* Content Column */}
        <div css={commentContentColumnStyles}>
          {/* Name, Time, and Actions Row */}
          <div css={commentNameTimeRowStyles}>
            <div css={commentNameContainerStyles}>
              <div css={commentNameAndTimeStyles}>
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.WHITE}
                >
                  {displayName}
                </Text>
              </div>
              <div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style='opacity: 0.5;'
                >
                  {formattedDate}
                </Text>
              </div>
            </div>
            <div css={commentActionsRowStyles}>
              {/* Like Button */}
              <div css={likeButtonStyles} onClick={handleLikeClick}>
                {isLiked ? (
                  <HeartFilled
                    style={{ color: colors.PRIMARY, fontSize: 16 }}
                  />
                ) : (
                  <HeartOutlined
                    style={{ color: colors.PRIMARY, fontSize: 16 }}
                  />
                )}
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.WHITE}
                >
                  {likesCount}
                </Text>
              </div>
              {/* More Icon */}
              <div css={commentMoreIconContainerStyles}>{renderMoreIcon()}</div>
            </div>
          </div>

          {/* Comment Text */}
          <div css={commentTextContainerStyles}>
            <div css={commentTextStyles}>{comment.body}</div>
          </div>
        </div>
      </div>
    )
  }
)

CommentItem.displayName = 'CommentItem'

// Add Comment Component
const AddCommentSection = memo<{
  userProfileSrc: string
  onCommentSubmit: (comment: string, postId: string) => Promise<void>
  postId: string
}>(({ userProfileSrc, onCommentSubmit, postId }) => {
  const [commentText, setCommentText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!commentText.trim() || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setError(null) // Clear any previous errors
    try {
      await onCommentSubmit(commentText.trim(), postId)
      setCommentText('')
    } catch (e) {
      console.error('Failed to submit comment:', e)
      setError('Failed to post comment. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [commentText, isSubmitting, onCommentSubmit, postId])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <div css={addCommentContainerStyles}>
      <img src={userProfileSrc} alt='User avatar' css={commentAvatarStyles} />
      <div css={commentInputContainerStyles}>
        <div
          css={css`
            position: relative;
          `}
          onKeyDown={handleKeyPress}
        >
          <Input.TextArea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder='Add a comment...'
            disabled={isSubmitting}
            autoSize={{ minRows: 1, maxRows: 6 }}
            css={css`
              background: ${colors.SECONDARY_200} !important;
              border: 1px solid ${colors.SECONDARY_100} !important;
              border-radius: 8px !important;
              padding: 12px 80px 12px 16px !important;
              color: ${colors.WHITE} !important;
              resize: none !important;

              &::placeholder {
                color: ${colors.NEUTRAL_50} !important;
              }

              &:focus {
                border-color: ${colors.PRIMARY} !important;
                box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.1) !important;
              }

              &:hover {
                border-color: ${colors.SECONDARY_100} !important;
              }
            `}
          />
          <button
            css={css`
              ${postButtonStyles}
              position: absolute;
              right: 8px;
              bottom: 8px;
              height: 32px;
            `}
            onClick={handleSubmit}
            disabled={!commentText.trim() || isSubmitting}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={
                !commentText.trim() || isSubmitting
                  ? colors.NEUTRAL_50
                  : colors.NEUTRAL_900
              }
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </Text>
          </button>
        </div>
        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            closable
            onClose={() => setError(null)}
            css={css`
              margin-top: 8px;
              .ant-alert {
                background: ${colors.SECONDARY_200} !important;
                border: 1px solid #ff4d4f !important;
                border-radius: 8px !important;
              }
              .ant-alert-message {
                color: ${colors.WHITE} !important;
                font-size: 14px !important;
              }
              .ant-alert-icon {
                color: #ff4d4f !important;
              }
              .ant-alert-close-icon {
                color: ${colors.WHITE} !important;
              }
            `}
          />
        )}
      </div>
    </div>
  )
})

AddCommentSection.displayName = 'AddCommentSection'

// Delete Comment Button component
const DeleteCommentButton = memo<React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ ...props }) => {
    return (
      <button
        css={css`
          display: flex;
          padding: 8px 16px 8px 8px;
          justify-content: center;
          align-items: center;
          gap: 8px;
          border-radius: 8px;
          border: 1px solid ${colors.ERROR_200};
          background: transparent;
          cursor: pointer;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
            : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'};
          display: none;
        `}
        {...props}
      >
        <DeleteOutlined
          css={css`
            color: ${colors.ERROR_200};
          `}
        />
        <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
          DELETE COMMENT
        </Text>
      </button>
    )
  }
)

DeleteCommentButton.displayName = 'DeleteCommentButton'

// Interfaces
interface FeedItemDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'team' | 'schedule' | 'organization'
  title: string
  body: string
  imageUrl?: string
  linkUrl?: string
  likesCount: number
  commentsCount?: number
  createdAt: string
  avatarUrl?: string
  onLikeClick: () => void
  entityName: string
  isAppPost?: boolean
  userAvatarUrl?: string
  entityId: string
  isLiked?: boolean
  userId?: string
  userName?: string
  userProfilePicture?: string
  onEditClick?: () => void
  comments?: Reply[]
  onReplySubmit?: (replyContent: string) => Promise<void>
  postId: string
  selfUserId?: string
  selfUserName?: string
  canReply?: boolean
  fetchPosts?: (pageNumber: number, shouldDisplayLoading?: boolean) => void
  role_types?: { id: number; name: string }[]
}

/**
 * FeedItemDetailsModal displays a feed item in a modal with detailed view including comments
 */
const FeedItemDetailsModal: React.FC<FeedItemDetailsModalProps> = ({
  isOpen,
  onClose,
  userProfilePicture,
  entityType,
  entityId,
  title,
  body,
  imageUrl,
  linkUrl,
  likesCount,
  createdAt,
  avatarUrl,
  onLikeClick,
  entityName,
  isAppPost = false,
  isLiked: initialIsLiked = false,
  commentsCount,
  comments = [],
  postId,
  selfUserId,
  selfUserName,
  canReply = true,
  fetchPosts,
  role_types = [],
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const originalUrlRef = useRef<string | null>(null)

  // Local state for optimistic UI updates
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [localComments, setLocalComments] = useState<Reply[]>(comments)
  const [localCommentsCount, setLocalCommentsCount] = useState(
    commentsCount || comments.length
  )
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()

  // URL management for modal state
  useEffect(() => {
    if (isOpen) {
      openModal()

      const currentPath = location.pathname

      // Store the URL to restore on close. When the modal was opened via a
      // shared deep link the current path already points at the post
      // (`/home/feed/:id` or `/{prefix}/{entityId}/feed/:id`); restoring that
      // verbatim would leave the post URL in place and re-open the modal on
      // reload/back. Normalize to the underlying feed path instead.
      originalUrlRef.current = currentPath.includes(`/feed/${postId}`)
        ? currentPath === `${ROUTES.HOME_ROOT}/feed/${postId}`
          ? ROUTES.HOME_ROOT
          : currentPath.replace(`/feed/${postId}`, '/feed')
        : currentPath

      // Check if we're on the /home path and need to update URL based on entity type
      if (currentPath === '/home') {
        const sharePath = buildPostShareUrl({
          origin: '',
          postId,
          entityType,
          entityId,
          isAppPost,
        })
        if (sharePath) {
          window.history.replaceState(null, '', sharePath)
        }
      }
      // Update URL if current path ends with /feed and doesn't already have the postId
      else if (
        currentPath.endsWith('/feed') &&
        !currentPath.includes(`/feed/${postId}`)
      ) {
        // Use replaceState to change URL without navigation
        window.history.replaceState(null, '', `${currentPath}/${postId}`)
      }
    } else {
      closeModal()
    }
  }, [
    closeModal,
    isOpen,
    openModal,
    location.pathname,
    postId,
    entityType,
    entityId,
    isAppPost,
  ])

  // Update local comments when props change
  useEffect(() => {
    setLocalComments(comments)
    setLocalCommentsCount(commentsCount || comments.length)
  }, [comments, commentsCount])

  // Scroll to bottom of comments when new comment is added
  const scrollToBottom = useCallback(() => {
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop =
        scrollableContentRef.current.scrollHeight
    }
  }, [])

  // Memoized computed values
  // const isOrgOrSchedulePost = useMemo(
  //   () => entityType === 'organization' || entityType === 'schedule',
  //   [entityType]
  // )

  const formattedHost = useMemo(() => {
    return linkUrl ? parseUrlHost(linkUrl) : null
  }, [linkUrl])

  const avatarSrc = useMemo(() => {
    if (isAppPost) {
      return isCanlan ? canlanLogo : sportninjaLogo
    }
    return avatarUrl || getPlaceholderImage(entityType)
  }, [isAppPost, avatarUrl, entityType])

  const userProfileSrc = useMemo(() => {
    return userProfilePicture || getPlaceholderImage(entityType, true)
  }, [userProfilePicture, entityType])

  const formattedDate = useMemo(() => {
    return getFeedPostDate(createdAt)
  }, [createdAt])

  const displayName = useMemo(() => {
    return entityName || 'SportNinja'
  }, [entityName])

  const entityLink = useMemo(() => {
    if (isAppPost || !entityId) {
      return null
    }
    return getEntityLink(entityType, entityId)
  }, [isAppPost, entityType, entityId])

  // Memoized event handlers
  const handleClose = useCallback(() => {
    // Restore the original URL when closing the modal
    if (originalUrlRef.current) {
      window.history.replaceState(null, '', originalUrlRef.current)
      originalUrlRef.current = null
    }

    // Refresh the feed to ensure we have the most up-to-date data
    if (fetchPosts) {
      fetchPosts(1, false)
    }

    onClose()
  }, [onClose, fetchPosts])

  const handleLikeClick = useCallback(() => {
    setIsLiked((prev) => !prev)
    onLikeClick()
  }, [onLikeClick])

  const shareUrl = buildPostShareUrl({
    origin: window.location.origin,
    postId,
    entityType,
    entityId,
    isAppPost,
  })

  const handleShareClick = useCallback(() => {
    if (shareUrl) {
      setIsShareModalOpen(true)
    }
  }, [shareUrl])

  const handleCommentLike = useCallback(async (commentId: string) => {
    try {
      // Optimistically update the local state
      setLocalComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                is_liked: true,
                like_count: (comment.like_count || 0) + 1,
              }
            : comment
        )
      )

      await likeComment(commentId)
    } catch (error) {
      console.error('Failed to like comment:', error)
      // Revert the optimistic update on error
      setLocalComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                is_liked: false,
                like_count: Math.max((comment.like_count || 0) - 1, 0),
              }
            : comment
        )
      )
    }
  }, [])

  const handleCommentDislike = useCallback(async (commentId: string) => {
    try {
      // Optimistically update the local state
      setLocalComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                is_liked: false,
                like_count: Math.max((comment.like_count || 0) - 1, 0),
              }
            : comment
        )
      )

      await dislikeComment(commentId)
    } catch (error) {
      console.error('Failed to dislike comment:', error)
      // Revert the optimistic update on error
      setLocalComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                is_liked: true,
                like_count: (comment.like_count || 0) + 1,
              }
            : comment
        )
      )
    }
  }, [])

  const handleDeleteComment = useCallback(
    async (_shouldForce = false) => {
      if (!commentToDelete) {
        throw new Error('No comment ID provided')
      }

      try {
        await deleteComment(commentToDelete)

        // Remove comment from local state
        setLocalComments((prev) =>
          prev.filter((comment) => comment.id !== commentToDelete)
        )
        setLocalCommentsCount((prev) => prev - 1)

        message.success('Comment deleted successfully.')
        setCommentToDelete(null)
      } catch (err: any) {
        console.error('Failed to delete comment:', err)
        message.error(err?.message || 'Failed to delete comment')
        throw err
      }
    },
    [commentToDelete, message]
  )

  const handleDeleteCommentClick = useCallback((commentId: string) => {
    setCommentToDelete(commentId)
    // Use a more reliable method to trigger the delete modal
    setTimeout(() => {
      document.getElementById('delete-comment-button-modal')?.click()
    }, 0)
  }, [])

  // Default comment submit handler with optimistic update
  const handleCommentSubmit = useCallback(
    async (commentText: string, _postId: string) => {
      // Create optimistic comment
      const optimisticComment: Reply = {
        id: `temp-${Date.now()}`, // Temporary ID
        body: commentText,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        social_feed_id: _postId,
        user: {
          id: 'current-user',
          email: '',
          name_full: 'You', // Will be replaced by actual user name if available
          image: userProfilePicture
            ? ({
                id: 'temp-image',
                filename: 'profile.jpg',
                full_path: userProfilePicture,
                mime_type: 'image/jpeg',
                width: 40,
                height: 40,
                formats: [],
              } as ImageInterface)
            : undefined,
        },
        like_count: 0,
        is_liked: false,
      }

      // Add optimistic comment immediately and update counter
      setLocalComments((prev) => [...prev, optimisticComment])
      setLocalCommentsCount((prev) => prev + 1)

      // Scroll to bottom after a short delay to ensure the comment is rendered
      setTimeout(() => {
        scrollToBottom()
      }, 100)

      try {
        const response = await req(`/social-feed/${_postId}/reply`, {
          method: 'POST',
          body: JSON.stringify({
            body: commentText,
            social_feed_id: _postId,
          }),
        })

        // Update the optimistic comment with the real response data
        if (response.data) {
          setLocalComments((prev) =>
            prev.map((comment) =>
              comment.id === optimisticComment.id
                ? {
                    ...response.data,
                    // Ensure we maintain the Reply interface structure
                    id: response.data.id,
                    body: response.data.body,
                    created_at: response.data.created_at,
                    updated_at: response.data.updated_at,
                    social_feed_id: response.data.social_feed_id,
                    user: response.data.user,
                    like_count: response.data.like_count,
                    is_liked: response.data.is_liked,
                  }
                : comment
            )
          )
        }
      } catch (error) {
        // Remove optimistic comment on error and revert counter
        setLocalComments((prev) =>
          prev.filter((comment) => comment.id !== optimisticComment.id)
        )
        setLocalCommentsCount((prev) => prev - 1)
        throw error // Re-throw to let AddCommentSection handle the error
      }
    },
    [userProfilePicture, scrollToBottom]
  )

  // Check if we have a valid image URL
  const hasValidImage = useMemo(() => {
    return imageUrl && imageUrl.trim() !== ''
  }, [imageUrl])

  return (
    <>
      <DeleteModal
        title='Delete Comment'
        message='Are you sure you want to delete this comment? This cannot be undone.'
        onDelete={handleDeleteComment}
        Button={(props) => (
          <DeleteCommentButton id='delete-comment-button-modal' {...props} />
        )}
        forceDeleteText={undefined}
        shouldCloseModalAfterDelete={true}
        useForceDelete={false}
        displayBanner={false}
      />

      <Modal
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        centered={!isMobile}
        styles={modalStyles}
        width={isMobile ? '100vw' : hasValidImage ? 1080 : 1080}
        css={css`
          display: flex;
          flex: 1;
          height: ${isMobile
            ? '100vh'
            : hasValidImage
            ? '90vh'
            : 'auto'} !important;
          max-height: ${isMobile ? '100vh' : '90vh'} !important;
          padding: 0;
          max-width: ${isMobile ? '100%' : hasValidImage ? '1080px' : '1080px'};

          ${isMobile
            ? `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            transform: none !important;
            margin: 0 !important;
            width: 100vw !important;
          `
            : `
            margin: 24px;
          `}

          .ant-modal-content {
            height: ${isMobile
              ? '100vh'
              : hasValidImage
              ? '90vh'
              : 'auto'} !important;
            max-height: ${isMobile ? '100vh' : '90vh'} !important;
            border-radius: ${isMobile ? '0' : '8px'};
            display: flex;
            flex-direction: column;
            padding: 0 !important;
            width: ${isMobile ? '100vw' : 'auto'} !important;
            margin: ${isMobile ? '0' : 'auto'} !important;
          }
          .ant-modal-body {
            flex: 1;
            overflow: ${isMobile
              ? 'hidden'
              : hasValidImage
              ? 'hidden'
              : 'visible'};
            display: flex;
            flex-direction: column;
            padding: 0 !important;
            height: ${isMobile ? '100vh' : 'auto'} !important;
          }
          .ant-tabs-tabpane {
            background: ${colors.SECONDARY_300} !important;
          }
          .ant-tabs-nav {
            min-height: ${isMobile ? 'auto' : '600px'} !important;
          }
          .ant-tabs-tab {
            height: 40px !important;
          }
          /* if the tab is active, change the background color */
          .ant-tabs-tab-active {
            background-color: #26303e;
          }
          .ant-tabs-tab-btn {
            ${paragraphMedium}
            ${weightRegular}
          }
          .ant-tabs-tab-btn[aria-selected='true'] {
            color: ${colors.PRIMARY}!important;
          }
          .ant-tabs-tab-btn[aria-selected='false'] {
            color: ${colors.NEUTRAL_30}!important;
          }
          .ant-tabs-tab.ant-tabs-tab-disabled {
            color: ${colors.NEUTRAL_30}!important;
            cursor: not-allowed !important;
            opacity: 0.5 !important;
          }
        `}
        classNames={classNames}
        maskClosable={false}
      >
        <div
          css={
            hasValidImage
              ? modalContentStyles
              : css`
                  overflow: hidden;
                  border-radius: ${isMobile ? '0' : '8px'};
                  display: flex;
                  flex-direction: column;
                  height: 100%;
                `
          }
        >
          {/* Conditionally render image section */}
          {hasValidImage && (
            <div css={imageContainerStyles}>
              <Image src={imageUrl} preview={false} draggable={false} />
            </div>
          )}

          {/* Content and Comments */}
          <div
            css={
              hasValidImage
                ? contentContainerStyles
                : contentOnlyContainerStyles
            }
          >
            {/* Header */}
            <div css={headerStyles}>
              <div css={profileInfoStyles}>
                {entityLink ? (
                  <Link
                    to={entityLink}
                    css={avatarLinkStyles}
                    onClick={handleClose}
                  >
                    <img src={avatarSrc} alt='Entity logo' css={avatarStyles} />
                  </Link>
                ) : (
                  <img src={avatarSrc} alt='Entity logo' css={avatarStyles} />
                )}
                <div css={userInfoStyles}>
                  <div css={nameRowStyles}>
                    {entityLink ? (
                      <Link
                        to={entityLink}
                        css={nameLinkStyles}
                        onClick={handleClose}
                      >
                        <Text
                          variant='paragraphMedium'
                          weight='bold'
                          color={colors.WHITE}
                        >
                          {displayName}
                        </Text>
                      </Link>
                    ) : (
                      <Text
                        variant='paragraphMedium'
                        weight='bold'
                        color={colors.WHITE}
                      >
                        {displayName}
                      </Text>
                    )}
                    {isAppPost && <VerifiedIcon />}
                  </div>
                  <ProfileMetadataRow
                    dateLabel={formattedDate}
                    role_types={role_types}
                  />
                </div>
              </div>
            </div>

            <div css={separatorStyles} />

            {/* Scrollable Content Area - Post Content and Comments */}
            <div css={scrollableContentStyles} ref={scrollableContentRef}>
              {/* Post Content */}
              <div css={postContentStyles(canReply)}>
                <div
                  css={css`
                    word-wrap: break-word;
                    word-break: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                  `}
                >
                  <Text
                    variant='paragraphMedium'
                    weight='bold'
                    style='font-size: 18px;'
                    color={colors.WHITE}
                  >
                    {title}
                  </Text>
                </div>
                <RichTextRenderer content={body} showReadMore={false} />
              </div>

              {canReply && (
                <>
                  <div css={separatorStyles} />

                  {/* Comments Section */}
                  <div css={commentsContainerStyles}>
                    <div css={commentsListStyles}>
                      {localComments.map((comment, index) => (
                        <React.Fragment key={comment.id}>
                          <CommentItem
                            comment={comment}
                            onLikeClick={handleCommentLike}
                            onDislikeClick={handleCommentDislike}
                            onDeleteClick={handleDeleteCommentClick}
                            selfUserId={selfUserId}
                            selfUserName={selfUserName}
                          />
                          {index < localComments.length - 1 && (
                            <div css={commentSeparatorStyles} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {canReply && <div css={separatorStyles} />}

            {/* Actions */}
            <div css={actionsContainerStyles}>
              <div css={actionsGroupStyles}>
                <div css={actionButtonStyles} onClick={handleLikeClick}>
                  {isLiked ? (
                    <HeartFilled
                      style={{ color: colors.PRIMARY, fontSize: 16 }}
                    />
                  ) : (
                    <HeartOutlined
                      style={{ color: colors.PRIMARY, fontSize: 16 }}
                    />
                  )}
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.WHITE}
                  >
                    {likesCount} {likesCount === 1 ? 'Like' : 'Likes'}
                  </Text>
                </div>
                {canReply && (
                  <div css={actionButtonStyles}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='17'
                      viewBox='0 0 16 17'
                      fill='none'
                    >
                      <path
                        d='M4.66671 9.83331H11.3334C11.5223 9.83331 11.6805 9.76931 11.808 9.64131C11.936 9.51376 12 9.35554 12 9.16665C12 8.97776 11.936 8.81931 11.808 8.69131C11.6805 8.56376 11.5223 8.49998 11.3334 8.49998H4.66671C4.47782 8.49998 4.31937 8.56376 4.19137 8.69131C4.06382 8.81931 4.00004 8.97776 4.00004 9.16665C4.00004 9.35554 4.06382 9.51376 4.19137 9.64131C4.31937 9.76931 4.47782 9.83331 4.66671 9.83331ZM4.66671 7.83331H11.3334C11.5223 7.83331 11.6805 7.76931 11.808 7.64131C11.936 7.51376 12 7.35554 12 7.16665C12 6.97776 11.936 6.81931 11.808 6.69131C11.6805 6.56376 11.5223 6.49998 11.3334 6.49998H4.66671C4.47782 6.49998 4.31937 6.56376 4.19137 6.69131C4.06382 6.81931 4.00004 6.97776 4.00004 7.16665C4.00004 7.35554 4.06382 7.51376 4.19137 7.64131C4.31937 7.76931 4.47782 7.83331 4.66671 7.83331ZM4.66671 5.83331H11.3334C11.5223 5.83331 11.6805 5.76954 11.808 5.64198C11.936 5.51398 12 5.35554 12 5.16665C12 4.97776 11.936 4.81931 11.808 4.69131C11.6805 4.56376 11.5223 4.49998 11.3334 4.49998H4.66671C4.47782 4.49998 4.31937 4.56376 4.19137 4.69131C4.06382 4.81931 4.00004 4.97776 4.00004 5.16665C4.00004 5.35554 4.06382 5.51398 4.19137 5.64198C4.31937 5.76954 4.47782 5.83331 4.66671 5.83331ZM13.5334 14.0333L12 12.5H2.66671C2.30004 12.5 1.98626 12.3695 1.72537 12.1086C1.46404 11.8473 1.33337 11.5333 1.33337 11.1666V3.16665C1.33337 2.79998 1.46404 2.48598 1.72537 2.22465C1.98626 1.96376 2.30004 1.83331 2.66671 1.83331H13.3334C13.7 1.83331 14.014 1.96376 14.2754 2.22465C14.5363 2.48598 14.6667 2.79998 14.6667 3.16665V13.55C14.6667 13.85 14.5307 14.0582 14.2587 14.1746C13.9863 14.2915 13.7445 14.2444 13.5334 14.0333ZM2.66671 3.16665V11.1666H12.55L13.3334 11.95V3.16665H2.66671Z'
                        fill={colors.PRIMARY}
                      />
                    </svg>
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.WHITE}
                    >
                      {localCommentsCount}{' '}
                      {localCommentsCount === 1 ? 'Comment' : 'Comments'}
                    </Text>
                  </div>
                )}
                <div css={actionButtonStyles} onClick={handleShareClick}>
                  <ShareIcon width={16} height={16} fill={colors.PRIMARY} />
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.WHITE}
                  >
                    Share
                  </Text>
                </div>
              </div>
              {formattedHost && (
                <div>
                  <a
                    href={linkUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    css={linkButtonStyles}
                  >
                    <ExternalLinkIcon
                      width={16}
                      height={16}
                      fill={colors.PRIMARY}
                    />
                    <Text
                      variant='paragraphXSmall'
                      weight='bold'
                      color={colors.PRIMARY}
                    >
                      {formattedHost}
                    </Text>
                  </a>
                </div>
              )}
            </div>

            {/* Add Comment */}
            {canReply && (
              <AddCommentSection
                userProfileSrc={userProfileSrc}
                onCommentSubmit={handleCommentSubmit}
                postId={postId}
              />
            )}
          </div>
        </div>
      </Modal>

      {postId && shareUrl && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          entityType='post'
          entityName={title || entityName}
          shareUrl={shareUrl}
        />
      )}
    </>
  )
}

export default memo(FeedItemDetailsModal)
